/* global SearchEnabled, ProfileEnabled, SingleMvpd, DebugPanelEnabled,
 EpgEnabled, RetiredShowsEnabled, CCPAEnabled */

import React from 'react';
import { Route, Redirect, IndexRoute } from 'react-router';
import App from 'app'; // eslint-disable-line
import { removeCookie, getCookie } from 'helpers/Storage';
import { mvpdDeeplinkEnabled } from 'helpers/ConfigHelper';
import { CCPA_VERIFY_COOKIE_NAME } from 'helpers/ContentHelper';
import { toBoolean } from 'helpers/GlobalHelper';
import { userAttributeEvents as optimizelyUserAttributeEvents } from 'helpers/OptimizelyHelper';
import { onRouteChange, helperGetBaseUrlSlug } from 'helpers/RouteHelper';
import { getDesignV2Routes, rootScreenUrl } from './routes.helper';

const keyGenerator = () => {
  let counter = 0;
  return () => {
    counter += 1;
    return `route-key-${counter}`;
  };
};
const routeKeyGen = keyGenerator();

const onHomeLoad = () => optimizelyUserAttributeEvents().home();
const onLiveLoad = () => {
  optimizelyUserAttributeEvents().live();
};

const onAccountLoad = () => {
  optimizelyUserAttributeEvents().settings();
};
// new relic script is not loaded in on ccpa/verify page. reload
// app when leaving this page so that new relic gets loaded in.
const onCCPAVerifyLeave = param => {
  const cookie = getCookie({ name: CCPA_VERIFY_COOKIE_NAME });
  const hasCookie = cookie !== undefined;
  if (hasCookie) {
    if (toBoolean(cookie)) {
      removeCookie({ key: CCPA_VERIFY_COOKIE_NAME });
      window.location.reload();
    }
  } else {
    const verifyPathname = param.location.pathname.replace(/\/$/, '');
    window.history.pushState({}, '', `${verifyPathname}${param.location.search}`);
  }
};
const baseUrlSlug = helperGetBaseUrlSlug();
const appViewRoute = `${baseUrlSlug}/app-view`;
const appRoute = baseUrlSlug || '/';
let baseUrlSlugRedirect = null;
if (baseUrlSlug.length > 0) {
  const redirectRoute = `${baseUrlSlug}/`;
  baseUrlSlugRedirect = <Redirect from="/" to={redirectRoute} />;
}

export default (screens = {}) => {
  const {
    AccountScreen,
    ArticleDetailScreen,
    CollectionScreen,
    ContentDetailScreen,
    DebugScreen,
    Error500Screen,
    Error404Screen,
    LatestEpisodeScreen,
    LiveScreen,
    LoadingScreen,
    MVPDScreen,
    ResetPasswordScreen,
    SearchScreen,
    ShowsScreen,
    SignInScreen,
    SiteMapScreen,
    SubscriptionScreen,
    WatchScreen,
  } = screens;

  const searchRoute = SearchEnabled && (
    <Route
      key={routeKeyGen()}
      path="find(/:findCollectionId)(/filter/:findFilterId)"
      component={SearchScreen}
    />
  );
  const accountRoute = ProfileEnabled && (
    <Route
      key={routeKeyGen()}
      path="account(/:overlay)"
      component={AccountScreen}
      onEnter={onAccountLoad}
    />
  );
  const verifyRoute = CCPAEnabled && (
    <Route
      key={routeKeyGen()}
      path="/ccpa/verify"
      component={AccountScreen}
      onEnter={onAccountLoad}
      onLeave={onCCPAVerifyLeave}
    />
  );
  const singleProviderSignInRoute = SingleMvpd && (
    <Route key={routeKeyGen()} path="/sign-in" component={SignInScreen} />
  );
  const debugRoute = DebugPanelEnabled && [
    <Route key={routeKeyGen()} path="debug" component={DebugScreen} />,
    <Route key={routeKeyGen()} path="debug/:panelId" component={DebugScreen} />,
  ];

  const defaultRoutes = [];
  let mvpdRoute;
  const RootScreen = CollectionScreen;
  const livePath = EpgEnabled ? 'live(/nav)(/schedule)(/channel/:callSign)' : 'live/:videoId';
  const liveRoute = (
    <Route key={routeKeyGen()} path={livePath} component={LiveScreen} onEnter={onLiveLoad} />
  );

  const designV2Routes = getDesignV2Routes();
  if (!EpgEnabled) designV2Routes.push(liveRoute);

  if (mvpdDeeplinkEnabled()) {
    mvpdRoute = <Route key={routeKeyGen()} path="tv-provider" component={MVPDScreen} />;
  }

  const retiredShowsRoute = RetiredShowsEnabled ? (
    <Route key={routeKeyGen()} path="/flashback" isRetiredShowsPage component={ShowsScreen} />
  ) : null;

  return (
    <Route key={routeKeyGen()} onChange={onRouteChange}>
      <Redirect from="/http://dev.fox-com.dws.la/*" to="/*" />
      <Redirect from="/http://qa.fox-com.dws.la/*" to="/*" />
      {baseUrlSlugRedirect}
      <Route key={routeKeyGen()} path={appViewRoute} appView component={App}>
        <IndexRoute component={RootScreen} onEnter={onHomeLoad} />
        <Route
          key={routeKeyGen()}
          name="reset-password"
          path="reset-password/:resetCode"
          component={ResetPasswordScreen}
        />
        <Route key={routeKeyGen()} path="article/:articleId" component={ArticleDetailScreen} />
      </Route>
      <Route key={routeKeyGen()} path={appRoute} component={App}>
        <IndexRoute component={RootScreen} onEnter={onHomeLoad} screenUrl={rootScreenUrl} />
        {designV2Routes}
        <Route
          key={routeKeyGen()}
          name="reset-password"
          appView
          path="reset-password/:resetCode"
          component={ResetPasswordScreen}
        />
        <Route
          key={routeKeyGen()}
          path="reset-password/fox-soul/:resetCode"
          component={ResetPasswordScreen}
        />
        <Route key={routeKeyGen()} path="sitemap" component={SiteMapScreen} />
        <Route
          key={routeKeyGen()}
          path="collection/:collectionId"
          component={RootScreen}
          onEnter={onHomeLoad}
          screenUrl={rootScreenUrl}
        />
        {mvpdRoute}
        {searchRoute}
        {accountRoute}
        {verifyRoute}
        {singleProviderSignInRoute}
        {defaultRoutes}
        <Route
          key={routeKeyGen()}
          path="profile-details(/:network)"
          component={RootScreen}
          screenUrl={rootScreenUrl}
        />
        <Route
          key={routeKeyGen()}
          path="forgot-password(/:network)"
          component={RootScreen}
          isForgotPassword
          screenUrl={rootScreenUrl}
        />
        <Route key={routeKeyGen()} path="watch/:videoId(/mode/:modeId)" component={WatchScreen} />
        <Route
          key={routeKeyGen()}
          path="full-episodes/latest/:showId"
          component={LatestEpisodeScreen}
        />
        {retiredShowsRoute}
        // TODO remove maskverse route in the future to be a subscription based route
        <Route key={routeKeyGen()} path="maskverse" component={SubscriptionScreen} />
        <Route key={routeKeyGen()} path="article/:articleId" component={ArticleDetailScreen} />
        <Route key={routeKeyGen()} path="article/:articleId/u" component={ArticleDetailScreen} />
        <Route key={routeKeyGen()} path="error.html" component={LoadingScreen} />
        <Route key={routeKeyGen()} path="maintenance.html" maintenance component={Error500Screen} />
        {debugRoute}
        <Route key={routeKeyGen()} path=":showId" component={ContentDetailScreen} />
        <Route key={routeKeyGen()} path=":showId/:pageType" component={ContentDetailScreen} />
        <Route
          key={routeKeyGen()}
          path=":showId/:pageType/:pageId"
          component={ContentDetailScreen}
        />
        <Route key={routeKeyGen()} path="*" component={Error404Screen} />
      </Route>
    </Route>
  );
};
